import React from 'react';

import { useHiveBranch, useHiveClient } from '@gitbook/hive-react';
import { toast } from '@gitbook/spine-react';

/**
 * When previewing a change request, we need to save the working changes made on the branch before we can show the preview.
 * This hook automatically saves the changes when navigating between editor and preview mode;
 * and returns the saved revision id.
 *
 * Returns `undefined` when the revision is being saved.
 */
export function useSavedRevision(primaryRevisionId: string) {
    const hiveClient = useHiveClient();
    const hiveBranch = useHiveBranch();

    const [savedRevisionId, setSavedRevisionId] = React.useState<string | undefined>(undefined);

    const wasSaved = !!savedRevisionId;
    const isFrozenContent = hiveBranch?.dataSync.readOnly;

    React.useEffect(() => {
        if (!hiveBranch || wasSaved || isFrozenContent) {
            return;
        }

        toast.catch(
            () =>
                hiveClient.sync.withReadOnlyDataSyncs(
                    async () => {
                        const result = await hiveClient.saveBranch(
                            hiveBranch.contentId,
                            hiveBranch.branchId
                        );
                        setSavedRevisionId(result.revisionId);
                    },
                    (sync) =>
                        sync.contentId === hiveBranch.contentId &&
                        sync.branchId === hiveBranch.branchId
                ),
            'Error while saving changes for preview'
        );
    }, [hiveClient, hiveBranch, wasSaved, isFrozenContent]);

    if (!hiveBranch || isFrozenContent) {
        // When frozen content, we directly use the revision id.
        return primaryRevisionId;
    }

    if (savedRevisionId) {
        return savedRevisionId;
    }
}
